import React, { useEffect } from "react";
import { Box, Container, Typography, CircularProgress } from "@mui/material";
import PublicNav from "./PublicNav";
import NewsTicker from "./NewsTicker";

interface NewsArticle {
  id: string;
  title: string;
  subtitle?: string;
}

interface PublicLayoutProps {
  children: React.ReactNode;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl" | false;
  lastUpdated?: Date | null;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  hideLastUpdated?: boolean;
  tickerArticles?: NewsArticle[];
}

const PublicLayout: React.FC<PublicLayoutProps> = ({
  children,
  maxWidth = "lg",
  lastUpdated,
  onRefresh,
  isRefreshing,
  hideLastUpdated = false,
  tickerArticles = []
}) => {
  // Disable copy, cut, paste, and right-click on public pages
  useEffect(() => {
    const preventCopy = (e: ClipboardEvent) => {
      e.preventDefault();
      return false;
    };

    const preventContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      return false;
    };

    const preventDragStart = (e: DragEvent) => {
      e.preventDefault();
      return false;
    };

    const preventSelectStart = (e: Event) => {
      e.preventDefault();
      return false;
    };

    // Add event listeners
    document.addEventListener("copy", preventCopy);
    document.addEventListener("cut", preventCopy);
    document.addEventListener("paste", preventCopy);
    document.addEventListener("contextmenu", preventContextMenu);
    document.addEventListener("dragstart", preventDragStart);
    document.addEventListener("selectstart", preventSelectStart);

    // Cleanup
    return () => {
      document.removeEventListener("copy", preventCopy);
      document.removeEventListener("cut", preventCopy);
      document.removeEventListener("paste", preventCopy);
      document.removeEventListener("contextmenu", preventContextMenu);
      document.removeEventListener("dragstart", preventDragStart);
      document.removeEventListener("selectstart", preventSelectStart);
    };
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        bgcolor: "grey.50",
      }}
    >
      <PublicNav
        lastUpdated={lastUpdated}
        onRefresh={onRefresh}
        isRefreshing={isRefreshing}
        hideLastUpdated={hideLastUpdated}
      />

      {/* Spacer for fixed header */}
      <Box sx={{ height: { xs: "56px", sm: "64px" } }} />

      {/* Last Updated Bar with Ticker */}
      <Box
        sx={{
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        {/* Last Updated Row */}
        {!hideLastUpdated && (
          <Container maxWidth="lg" disableGutters sx={{ px: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                py: 0.5,
              }}
            >
              {/* Right side - Updated at */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {/* Updated at time */}
              <Typography
                variant="caption"
                sx={{
                  fontSize: { xs: "0.65rem", sm: "0.7rem" },
                  color: "text.secondary",
                }}
              >
                {lastUpdated ? "Updated at" : "Updated"}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontSize: { xs: "0.65rem", sm: "0.7rem" },
                  color: "text.primary",
                  fontWeight: 500,
                }}
              >
                {(() => {
                  if (!lastUpdated) return "just now";
                  // Format as HH:MM AM/PM
                  return lastUpdated.toLocaleString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                  });
                })()}
              </Typography>

              {/* Separator */}
              <Typography
                variant="caption"
                sx={{
                  fontSize: { xs: "0.65rem", sm: "0.7rem" },
                  color: "text.secondary",
                  mx: 0.5,
                }}
              >
                |
              </Typography>

              {/* Refresh button or progress indicator */}
              {isRefreshing ? (
                <CircularProgress size={12} sx={{ color: "primary.main" }} />
              ) : (
                <Typography
                  component="button"
                  onClick={onRefresh}
                  disabled={isRefreshing}
                  variant="caption"
                  sx={{
                    fontSize: { xs: "0.65rem", sm: "0.7rem" },
                    color: "primary.main",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: 0,
                    textDecoration: "underline",
                    fontWeight: 500,
                    "&:hover": {
                      color: "primary.dark",
                    },
                    "&:disabled": {
                      color: "text.disabled",
                      cursor: "not-allowed",
                    },
                  }}
                >
                  Refresh
                </Typography>
              )}
              </Box>
            </Box>
          </Container>
        )}

        {/* News Ticker */}
        {tickerArticles.length > 0 && <NewsTicker articles={tickerArticles} />}
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          py: 4,
        }}
      >
        <Container maxWidth={maxWidth}>
          {children}
        </Container>
      </Box>

      <Box
        component="footer"
        sx={{
          py: 3,
          px: 2,
          mt: "auto",
          bgcolor: (theme) => {
            // Convert hex to RGB and blend with white (10% primary, 90% white)
            const hex = theme.palette.primary.main.replace("#", "");
            const r = parseInt(hex.substr(0, 2), 16);
            const g = parseInt(hex.substr(2, 2), 16);
            const b = parseInt(hex.substr(4, 2), 16);
            // Blend: 10% primary + 90% white
            const blendedR = Math.round(r * 0.1 + 255 * 0.9);
            const blendedG = Math.round(g * 0.1 + 255 * 0.9);
            const blendedB = Math.round(b * 0.1 + 255 * 0.9);
            return `rgb(${blendedR}, ${blendedG}, ${blendedB})`;
          },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", color: "primary.main", fontSize: "0.75rem" }}>
            © {new Date().getFullYear()} https://superfastlive.com. All rights reserved.
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default PublicLayout;
